import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, TrendingUp, Activity, Crosshair, FileSearch, Users } from "lucide-react";
import { fetchConfig, fetchPicks, fetchStats, fmtSigned } from "@/lib/api";
import Stars from "@/components/Stars";
import Odds from "@/components/Odds";

const STRIP = [
  { icon: Crosshair, label: "NBA Picks" },
  { icon: Activity, label: "Player Props" },
  { icon: FileSearch, label: "Analysis" },
  { icon: TrendingUp, label: "Transparent Results" },
  { icon: Users, label: "VIP Community" },
];

function BestBetCard() {
  const { data, isLoading } = useQuery({
    queryKey: ["best-bet"],
    queryFn: () => fetchPicks({ best_bet: true, status: "pending", limit: 1 }),
  });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  const pick = data?.picks?.[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.25 }}
      className="panel relative overflow-hidden p-5 sm:p-6 shadow-[0_0_60px_rgba(0,230,118,0.07)]"
      data-testid="hero-best-bet-card"
    >
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-[#00E676]/10 blur-3xl" aria-hidden="true" />
      <div className="flex items-center justify-between">
        <span className="tag border border-[#00E676]/40 bg-[#00E676]/10 text-[#00E676]">
          <span className="live-dot h-1.5 w-1.5 rounded-full bg-[#00E676]" /> Tonight's Best Bet
        </span>
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{pick ? (pick.visibility === "vip" ? "VIP pick" : "Free pick") : "Official picks only"}</span>
      </div>

      {pick ? (
        <>
          <p className="mt-5 font-mono text-[11px] uppercase tracking-wider text-slate-500">{pick.game}</p>
          <h3 className="mt-1 font-display text-3xl font-bold uppercase leading-none text-white">{pick.player}</h3>
          <p className="font-display text-xl font-semibold uppercase text-[#00E676]">{pick.selection}</p>

          <div className="mt-5 grid grid-cols-3 gap-3 border-y border-[#1E2738] py-3 font-mono text-xs">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Odds</p>
              <p className="mt-0.5 text-sm font-bold text-white"><Odds value={pick.odds} /></p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Stake</p>
              <p className="mt-0.5 text-sm font-bold text-white">{pick.units} Unit</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500">Confidence</p>
              <div className="mt-1"><Stars value={pick.confidence} /></div>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-slate-500">
              <span>Owner confidence</span>
              <span className="text-[#00E676]">{pick.confidence * 20}%</span>
            </div>
            <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#1E2738]">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${pick.confidence * 20}%` }}
                transition={{ duration: 1, delay: 0.6 }}
                className="h-full rounded-full bg-[#00E676]"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center gap-1.5">
            {stats?.last10 && stats.last10 !== "0-0" ? (
              <>
                <span className="font-mono text-xs font-bold text-[#00E676]">{stats.last10}</span>
                <span className="ml-2 font-mono text-[10px] uppercase tracking-wider text-slate-500">Last 10 settled · official ledger</span>
              </>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Form builds as picks settle</span>
            )}
          </div>

          <p className="mt-4 text-sm leading-relaxed text-slate-400">{pick.analysis_preview}</p>
        </>
      ) : isLoading ? (
        <div className="mt-6 space-y-3" aria-busy="true">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-4 animate-pulse rounded bg-[#1E2738]" />
          ))}
        </div>
      ) : (
        <div className="mt-6" data-testid="hero-best-bet-empty">
          <p className="font-display text-xl font-bold uppercase text-white">No best bet published yet</p>
          <p className="mt-2 text-sm leading-relaxed text-slate-400">Every pick is researched and published by the owner before tip-off. Check the upcoming picks, or join VIP to be notified the moment the next best bet drops.</p>
          <Link to="/picks" className="btn-outline mt-5 !py-2.5 text-xs" data-testid="hero-best-bet-picks-link">View upcoming picks</Link>
        </div>
      )}
    </motion.div>
  );
}

export function Hero() {
  const { data: config } = useQuery({ queryKey: ["config"], queryFn: fetchConfig, staleTime: 300_000 });
  const { data: stats } = useQuery({ queryKey: ["stats"], queryFn: fetchStats });
  const vipUrl = config?.whop?.vip_checkout_url || "/#pricing";
  const freeUrl = config?.whop?.free_checkout_url || "/#pricing";

  const trust = stats
    ? [
        { label: "Season Record", value: stats.record },
        { label: "Net Units", value: fmtSigned(stats.net_units, "U") },
        { label: "Win Rate", value: `${stats.win_pct}%` },
        { label: "Last 10", value: stats.last10 },
      ]
    : [];

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(0,230,118,0.08),transparent_55%)]" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-16 pt-14 sm:px-6 sm:pt-20 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <div className="lg:col-span-7">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">
            2026–27 NBA Season · Analytics-First Picks
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-4 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight text-white sm:text-6xl lg:text-7xl"
          >
            Print<br />Smarter<span className="text-[#00E676]">.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.28 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg"
          >
            NBA picks backed by research, matchup analysis and transparent results.
            <span className="text-slate-200"> We don't sell guarantees. We show the work.</span>
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a href={vipUrl} target={vipUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="hero-join-vip-btn" className="btn-primary">
              Join VIP <ArrowRight size={16} />
            </a>
            <a href={freeUrl} target={freeUrl.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" data-testid="hero-free-picks-btn" className="btn-outline">
              Get Free Picks
            </a>
          </motion.div>

          {trust.length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-10 grid max-w-xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-[#1E2738] bg-[#1E2738] sm:grid-cols-4"
              data-testid="hero-trust-strip"
            >
              {trust.map((t) => (
                <div key={t.label} className="bg-[#0F131D] px-4 py-3">
                  <p className="font-mono text-base font-bold text-[#00E676]">{t.value}</p>
                  <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">{t.label}</p>
                </div>
              ))}
            </motion.div>
          )}
          <p className="mt-3 font-mono text-[10px] uppercase tracking-wider text-slate-600" data-testid="hero-stats-note">
            {stats?.demo ? "Stats shown are demo data until launch — real tracking starts day one." : "Official record — every settled Money Printer pick, wins and losses."}
          </p>
        </div>

        <div className="lg:col-span-5">
          <BestBetCard />
        </div>
      </div>

      <div className="border-y border-[#1E2738] bg-[#0A0D14]" data-testid="feature-strip">
        <div className="overflow-hidden py-3.5">
          <div className="marquee-track flex w-max items-center gap-10 pr-10">
            {[...STRIP, ...STRIP, ...STRIP].map((f, i) => (
              <span key={i} className="flex items-center gap-2.5 font-display text-sm font-bold uppercase tracking-widest text-slate-400">
                <f.icon size={15} className="text-[#00E676]" /> {f.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <Link to="/picks" data-testid="hero-view-board-link" className="font-mono text-xs uppercase tracking-wider text-slate-500 transition-colors hover:text-[#00E676]">
          View tonight's full board ↓
        </Link>
      </div>
    </section>
  );
}

export default Hero;
